/**
 * Pure helpers for the in-app dialer's mic permission flow.
 * Kept dependency-free (no react-router, axios, toast) so the helpers can
 * be unit-tested in isolation without dragging in the rest of App.
 */

/** Detect Android browser/WebView */
export const isAndroid = () => /android/i.test(navigator.userAgent);

/** Persistent cache key — once the user grants mic for this origin we
    never re-prompt them on the same browser/WebView session. Cleared by
    the browser when the origin's data is wiped. */
export const MIC_GRANT_CACHE_KEY = "fz_mic_permission_granted_v1";

/** Are we running inside the native React Native WebView wrapper? The
    wrapper handles the OS-level mic grant + the WebView permission gate
    natively, so the web side should NOT call getUserMedia again — that
    causes a redundant prompt on Android and sometimes a NotAllowedError
    if the WebView's perm-cache is in a transient state. */
export const isInNativeWrapper = () => {
  try {
    if (typeof window !== "undefined" && window.FieldzNative?.isNativeApp) return true;
    if (typeof localStorage !== "undefined" && localStorage.getItem("is_native_app") === "true") return true;
  } catch {
    /* defensive: localStorage can throw in some private-mode browsers */
  }
  return false;
};

/** Check if mic permission is available, request if not. Returns true when
 *  the dialer can proceed and false otherwise. Persists the granted state
 *  so subsequent loads on the same origin skip the prompt entirely. */
export const requestMicPermission = async () => {
  // Inside the native wrapper, the host app has already requested
  // RECORD_AUDIO + handled WebView's onPermissionRequest. Trust it and
  // skip the web-side getUserMedia probe so we don't double-prompt.
  if (isInNativeWrapper()) return true;

  // Honour our persistent cache so returning users skip the probe.
  try {
    if (localStorage.getItem(MIC_GRANT_CACHE_KEY) === "granted") return true;
  } catch {
    /* ignore */
  }

  try {
    // On Android WebViews and some Android Chrome versions,
    // navigator.permissions.query({ name: "microphone" }) throws TypeError.
    // Skip the permissions API entirely on Android — go straight to getUserMedia.
    if (!isAndroid() && navigator.permissions) {
      try {
        const perm = await navigator.permissions.query({ name: "microphone" });
        if (perm.state === "granted") {
          try { localStorage.setItem(MIC_GRANT_CACHE_KEY, "granted"); } catch { /* ignore */ }
          return true;
        }
        if (perm.state === "denied") return false;
        // "prompt" — fall through to getUserMedia
      } catch {
        // permissions.query not supported for "microphone" — fall through
      }
    }

    // Actually request mic access — this is the reliable path on all platforms.
    // On Android native WebView the OS-level permission dialog will appear
    // (or the native app has already granted it).
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    stream.getTracks().forEach(t => t.stop());
    try { localStorage.setItem(MIC_GRANT_CACHE_KEY, "granted"); } catch { /* ignore */ }
    return true;
  } catch (err) {
    // NotAllowedError = user denied, NotFoundError = no mic hardware
    // eslint-disable-next-line no-console
    console.error("Microphone permission error:", err.name, err.message);
    return false;
  }
};
